<template>
  <div no-gutters class="pa-0 tw:text-xxs tw:flexrow-3 tw:items-center tw:text-gray-500 tw:text-nowrap">
    <!-- COORDINATE SYSTEM MENU -->
    <v-menu
      ref="menu"
      max-height="250"
      :class="{ 'no-print': isQuickPrint }"
      offset-y
      :nudge-top="10"
      top
      :close-on-content-click="false"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-tooltip top color="font">
          <template #activator="{ on: onTooltip }">
            <v-row
              no-gutters
              class="align-center tw:flex-nowrap epsgMenu"
              v-on="{ ...on, ...onTooltip }"
              style="cursor: pointer"
            >
              <v-col cols="auto">
                {{ cursorPositionCoordinateSystemName }}
              </v-col>
              <v-col cols="auto">
                <v-icon size="18" :style="attrs['aria-expanded'] === 'true' ? 'transform: rotate(180deg)' : ''">
                  mdi-menu-down
                </v-icon>
              </v-col>
            </v-row>
          </template>
          {{ $i18n.t('tooltips.coordinatesSystem') }}
        </v-tooltip>
      </template>
      <v-card class="pa-3 epsg-menu">
        <v-radio-group v-model="cursorPositionEpsg" column class="ma-0" hide-details>
          <template v-for="epsg in arrayEpsg">
            <v-radio on-icon="mdi-check" off-icon="" :value="epsg.value" :key="epsg.value" color="font" :ripple="false">
              <template #label>
                <v-subheader class="text-caption pa-0 font--text" style="height: auto">
                  {{ epsg.text }}
                </v-subheader>
              </template>
            </v-radio>
          </template>
        </v-radio-group>
      </v-card>
    </v-menu>

    <!-- MOUSE COORDINATES -->
    <v-tooltip top color="font">
      <template #activator="{ on }">
        <div v-on="on" id="mousePosition" :class="{ 'no-print': isQuickPrint }"></div>
      </template>
      {{ $i18n.t('tooltips.mousePosition') }}
    </v-tooltip>

    <!-- MAP ROTATION -->
    <div :class="{ 'no-print': isQuickPrint }" v-if="mapRotationModuleEnabled" class="tw:flexrow-0 tw:items-center">
      <span>{{ `${$i18n.t('navbar.mapRotationAngle')}: ` }}</span>
      <data-input
        class="ml-1 rotate-map-input"
        v-model="mapRotation"
        :data-type="{ name: 'integer' }"
        :minValue="0"
        :maxValue="360"
        :allow-negative="false"
        hide-arrows
        hide-details
        @keyup.enter="changeMapRotation"
      />
      <span class="mr-1 tw:text-base/1">°</span>
    </div>

    <!-- NUMERICAL SCALE -->
    <v-tooltip top color="font">
      <template #activator="{ on }">
        <div v-on="on" class="tw:flexrow-0 tw:items-center">
          <span class="mr-1">1:{{ scale }}</span>
          (
          <span>
            <data-input
              v-if="isEditZoomLevelModuleEnabled"
              class="zoom-level-input"
              v-model="zoomLevel"
              :data-type="{ name: 'integer' }"
              :allow-negative="false"
              hide-arrows
              hide-details
              @keyup.enter="changeZoomLevel"
            />
            <template v-else>{{ zoom.toFixed() }}</template>
          </span>
          )
        </div>
      </template>
      {{ $i18n.t('navbar.numericalScaleTooltip') }}
    </v-tooltip>

    <!-- LINEAR SCALE -->
    <v-tooltip top color="font">
      <template #activator="{ on }">
        <div v-on="on" id="scale" :class="{ 'print-scale': isQuickPrint }"></div>
      </template>
      {{ $i18n.t('tooltips.linearScale') }}
    </v-tooltip>
  </div>
</template>

<script>
import { get, sync } from 'vuex-pathify';

import { defaultMinZoom, defaultMaxZoom } from '../assets/js/variables';

import DataInput from '@/components/DataInput';

export default {
  name: 'TheNavbarToolsPanelMapInfo',
  components: {
    DataInput,
  },
  data: () => ({
    mapRotation: 0,
    zoomLevel: null,
  }),
  computed: {
    coordinatesEpsgs: get('map/coordinatesEpsgs'),
    cursorPositionEpsg: sync('map/cursorPositionEpsg'),
    isFetched: get('sidebar/isFetched'),
    isQuickPrint: get('tools/toolStatus@isQuickPrintToolActive'),
    isEditZoomLevelModuleEnabled: get('admin/additionalModules@EDIT_ZOOM_LEVEL.enabled'),
    mapRotationModuleEnabled: get('admin/additionalModules@MAP_ROTATION_MODULE.enabled'),
    arrayEpsg() {
      return Object.keys(this.coordinatesEpsgs).reduce((total, current) => {
        return [
          ...total,
          { text: this.coordinatesEpsgs[current].name, value: current, def: this.coordinatesEpsgs[current].proj4text },
        ];
      }, []);
    },
    cursorPositionCoordinateSystemName() {
      return this.coordinatesEpsgs[this.cursorPositionEpsg]
        ? this.coordinatesEpsgs[this.cursorPositionEpsg].name
        : this.$_config.defaultEpsgName;
    },
    scale: get('map/scale'),
    zoom: sync('map/zoom'),
    formattedScale() {
      return `1:${this.scale} (${this.zoom.toFixed()})`;
    },
  },
  methods: {
    changeZoomLevel() {
      if (!isNaN(this.zoomLevel)) {
        if (this.zoomLevel >= defaultMinZoom && this.zoomLevel <= defaultMaxZoom) {
          this.zoom = this.zoomLevel;
        } else if (this.zoomLevel < defaultMinZoom) {
          this.zoom = defaultMinZoom;
          this.zoomLevel = defaultMinZoom;
        } else if (this.zoomLevel > defaultMaxZoom) {
          this.zoom = defaultMaxZoom;
          this.zoomLevel = defaultMaxZoom;
        }
      } else {
        this.zoomLevel = null;
      }
    },
    changeMapRotation() {
      if (this.mapRotation >= 0 && this.mapRotation <= 360) {
        this.$root.$emit('rotateMap', JSON.parse(JSON.stringify(this.mapRotation)));
      }
    },
  },
  watch: {
    cursorPositionEpsg: {
      async handler(nV) {
        if (!this.coordinatesEpsgs[nV]) this.cursorPositionEpsg = this.$_config.defaultEpsg;
        await this.$nextTick();
        this.$refs.menu.updateDimensions();
      },
      immediate: true,
    },
    zoom: {
      handler(nV) {
        if (!nV && nV !== 0) return;
        this.zoomLevel = nV.toFixed();
      },
      immediate: true,
    },
  },
  mounted() {
    this.$root.$off('setRotation');
    this.$root.$on('setRotation', rotation => {
      this.mapRotation = rotation;
    });
    if (this.mapRotationModuleEnabled) {
      const mapRotation = +(localStorage.getItem('rotation') || 0);
      this.mapRotation = mapRotation;
    }
  },
};
</script>

<style scoped>
@reference '@/tailwind.css';

.epsg-menu :deep(.v-input__slot) {
  width: auto;
  max-width: 270px;
  padding-right: 0.8rem !important;
}
:deep(.scale) {
  line-height: normal;
  @apply tw:border-b tw:border-gray-500 tw:relative;
}
:deep(.scale::before) {
  content: '';
  @apply tw:border-x tw:border-gray-500 tw:absolute tw:h-1/2 tw:bottom-0 tw:left-0 tw:right-0;
}

:deep(.scale::after) {
  opacity: 0;
  content: '';
}
:deep(.v-input__slot) {
  font-size: 10px;
  height: 18px;
  min-height: 18px !important;
  padding: 0 2px !important;
}
.zoom-level-input :deep(.v-input__control .v-input__slot) {
  width: 18px;
}
.rotate-map-input :deep(.v-input__control .v-input__slot) {
  width: 35px;
}
:deep(.v-input__control .v-input__slot input) {
  text-align: center;
  padding: 0 !important;
}
</style>
